/**
 * Renter-facing wording for the delivery refusals the server answers with a
 * `code` (soft holds, bookings, pricing preview). Anything else keeps the
 * server's own message.
 */

export type DeliveryRecovery = "pickup" | "locations";

interface DeliveryErrorInfo {
  message: string;
  /** What the renter can do in one click to get out of the refusal. */
  recovery: DeliveryRecovery;
}

const DELIVERY_ERRORS: Record<string, DeliveryErrorInfo> = {
  delivery_not_available: {
    message:
      "Delivery is no longer available for this vehicle. Please choose pickup.",
    recovery: "pickup",
  },
  custom_address_not_available: {
    message:
      "This vehicle delivers only to its listed delivery locations. Please choose one of them or switch to pickup.",
    recovery: "locations",
  },
  distance_exceeds_max_delivery_radius: {
    message:
      "That address is too far from this vehicle for delivery. Please try a closer address or switch to pickup.",
    recovery: "pickup",
  },
  invalid_delivery_options: {
    message:
      "Your delivery details are incomplete or no longer valid. Please review them or choose pickup.",
    recovery: "pickup",
  },
  incomplete_delivery_details: {
    message:
      "Your delivery details are incomplete. Please review them or choose pickup.",
    recovery: "pickup",
  },
  delivery_location_not_found: {
    message:
      "That delivery location is no longer available. Please choose another one or switch to pickup.",
    recovery: "locations",
  },
};

export function getDeliveryErrorInfo(
  code: string | undefined | null,
): DeliveryErrorInfo | undefined {
  return code ? DELIVERY_ERRORS[code] : undefined;
}

// For a surface whose only recovery is pickup, a message that sends the renter
// to the locations would promise what it cannot offer.
const PICKUP_ONLY_MESSAGES: Record<string, string> = {
  custom_address_not_available:
    "This vehicle can no longer be delivered to a typed address. Please switch to pickup.",
  delivery_location_not_found:
    "That delivery location is no longer available. Please switch to pickup.",
};

/**
 * The renter-facing message of a delivery refusal. Where the renter cannot
 * choose a location from the surface (`canChooseLocation` false), only
 * pickup is promised.
 */
export function getDeliveryErrorMessage(
  code: string | undefined | null,
  canChooseLocation: boolean,
): string | undefined {
  const info = getDeliveryErrorInfo(code);
  if (!info) return undefined;
  return (
    (!canChooseLocation && code ? PICKUP_ONLY_MESSAGES[code] : undefined) ??
    info.message
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

// The GraphQL surface answers with these instead of the reason names.
const GRAPHQL_CODE_ALIASES: Record<string, string> = {
  "FMSAPI-DL-001": "delivery_location_not_found",
  "FMSAPI-DL-002": "delivery_not_available",
  "FMSAPI-DL-003": "custom_address_not_available",
  "FMSAPI-DL-004": "distance_exceeds_max_delivery_radius",
  "FMSAPI-DL-005": "invalid_delivery_options",
};

// The pricing preview answers these two refusals without a `code`
// (PricingController.render_error/3), so the exact message identifies them.
const CODE_LESS_MESSAGES: Record<string, string> = {
  "invalid delivery options": "invalid_delivery_options",
  "delivery location not found": "delivery_location_not_found",
};

/** The bodies a failed request can carry, wherever the client left them. */
function bodiesOf(error: unknown): unknown[] {
  const record = asRecord(error);
  if (!record) return [];
  const bodies: unknown[] = [
    record,
    record.data,
    asRecord(record.response)?.data,
  ];
  if (typeof record.message === "string") {
    try {
      bodies.push(JSON.parse(record.message));
    } catch {
      // not JSON: a plain message
    }
  }
  return bodies;
}

function codeOf(body: unknown): string | undefined {
  const record = asRecord(body);
  if (!record) return undefined;
  if (typeof record.code === "string") return record.code;
  // `{ error: { code } }` (FallbackController) and `{ errors: { code } }`
  // (bookings).
  for (const nested of [record.error, record.errors]) {
    const inner = asRecord(nested);
    if (inner && typeof inner.code === "string") return inner.code;
  }
  return undefined;
}

function messageOf(body: unknown, allowOwnMessage = true): string | undefined {
  const record = asRecord(body);
  if (!record) return undefined;
  // `{ error: "text" }` (soft holds), `{ error: { message } }`
  // (FallbackController), `{ errors: { detail } }` (bookings).
  if (typeof record.error === "string") return record.error;
  const inner = asRecord(record.error) ?? asRecord(record.errors);
  if (inner && typeof inner.message === "string") return inner.message;
  if (inner && typeof inner.detail === "string") return inner.detail;
  if (allowOwnMessage && typeof record.message === "string")
    return record.message;
  return undefined;
}

/**
 * Finds the server's `code` wherever the client left it: on an ApiError's
 * `data`, on an axios-style `response.data`, in a JSON-encoded message, or on
 * a plain `{ success: false, error, code }` result. GraphQL `FMSAPI-DL-00x`
 * codes are normalized to the reason names, and the two pricing refusals that
 * carry no code are recognized by their message.
 */
export function extractErrorCode(error: unknown): string | undefined {
  const bodies = bodiesOf(error);
  for (const body of bodies) {
    const code = codeOf(body);
    if (code) return GRAPHQL_CODE_ALIASES[code] ?? code;
  }
  for (const body of bodies) {
    const message = messageOf(body)?.trim().toLowerCase();
    if (message && CODE_LESS_MESSAGES[message])
      return CODE_LESS_MESSAGES[message];
  }
  return undefined;
}

/**
 * The server's human message, whichever body shape carried it. An ApiError's
 * own `message` is the JSON of an object `error`, so the body is read first.
 */
export function extractErrorMessage(error: unknown): string | undefined {
  // The error object itself is not a body: its `message` is the JSON text.
  const [self, ...bodies] = bodiesOf(error);
  const own = messageOf(self, false);
  if (own) return own;
  for (const body of bodies) {
    const message = messageOf(body);
    if (message) return message;
  }
  // The Error's own message is the JSON text when the client stringified an
  // object body (`{ errors: { field: [...] } }`): that is not for the renter.
  const text = asRecord(error)?.message;
  if (typeof text !== "string" || !text) return undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === "object" ? undefined : text;
  } catch {
    return text;
  }
}

const QUOTE_DELIVERY_MESSAGE =
  "Delivery can no longer be booked for this quote. Please contact the fleet to update it.";

/**
 * Wording for a quote the renter can only pay: unlike the checkout they
 * cannot change the delivery themselves, so the way out is the fleet.
 */
export function getQuoteDeliveryErrorMessage(
  code: string | undefined | null,
): string | undefined {
  if (!getDeliveryErrorInfo(code)) return undefined;
  return code === "distance_exceeds_max_delivery_radius"
    ? "The delivery address on this quote is now outside the vehicle's delivery area. Please contact the fleet to update it."
    : QUOTE_DELIVERY_MESSAGE;
}
